import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export type DateRangePreset = '7d' | '30d' | '90d' | 'all';

export interface DateRange {
  startDate?: Date;
  endDate?: Date;
}

interface DateRangeSelectorProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  preset?: DateRangePreset;
  onPresetChange?: (preset: DateRangePreset) => void;
}

export function DateRangeSelector({
  value,
  onChange,
  preset = 'all',
  onPresetChange,
}: DateRangeSelectorProps) {
  const [isCustomOpen, setIsCustomOpen] = useState(false);

  const handlePresetChange = (newPreset: DateRangePreset) => {
    if (onPresetChange) {
      onPresetChange(newPreset);
    }

    const endDate = new Date();
    let startDate: Date | undefined;

    switch (newPreset) {
      case '7d':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30d':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        break;
      case '90d':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 90);
        break;
      case 'all':
        startDate = undefined;
        break;
    }

    onChange({ startDate, endDate });
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <Select value={preset} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-full sm:w-[180px]">
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="7d">Last 7 days</SelectItem>
          <SelectItem value="30d">Last 30 days</SelectItem>
          <SelectItem value="90d">Last 90 days</SelectItem>
          <SelectItem value="all">All time</SelectItem>
        </SelectContent>
      </Select>

      <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full sm:w-[280px] justify-start text-left font-normal',
              !value.startDate && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value.startDate ? (
              value.endDate ? (
                <>
                  {format(value.startDate, 'LLL dd, y')} -{' '}
                  {format(value.endDate, 'LLL dd, y')}
                </>
              ) : (
                format(value.startDate, 'LLL dd, y')
              )
            ) : (
              <span>Custom range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={{
              from: value.startDate,
              to: value.endDate,
            }}
            onSelect={(range) => {
              if (range?.from) {
                onChange({
                  startDate: range.from,
                  endDate: range.to || range.from,
                });
                if (range.to) {
                  setIsCustomOpen(false);
                }
              }
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}







